<template>
  <div class="dh-search-form">
    <!-- 多行布局 -->
    <template v-if="useMultiRowLayout">
      <div class="multi-row-layout">
        <div
          v-for="(rowGroup, rowIndex) in rowGroups"
          :key="rowIndex"
          class="search-row"
          :style="{ justifyContent: rowGroup.justify || 'start' }"
        >
          <!-- 搜索字段 -->
          <div
            v-for="field in rowGroup.fieldConfigs"
            :key="field.key"
            class="search-item"
            :style="itemStyle"
          >
            <!-- 字段标题 -->
            <div v-if="field.label" class="search-label">{{ field.label }}</div>

            <!-- 输入框 -->
            <ElInput
              v-if="field.type === 'input'"
              v-model="searchForm[field.key]"
              :placeholder="field.placeholder"
              clearable
              @keyup.enter="handleSearch"
            />

            <!-- 选择框（自研下拉选择组件） -->
            <DhSelect
              v-else-if="field.type === 'select'"
              v-model="searchForm[field.key]"
              :options="field.options || []"
              :placeholder="field.placeholder"
            />

            <!-- 日期选择器 -->
            <ElDatePicker
              v-else-if="field.type === 'date'"
              v-model="searchForm[field.key]"
              type="date"
              :placeholder="field.placeholder"
              clearable
            />

            <!-- 日期范围选择器 -->
            <ElDatePicker
              v-else-if="field.type === 'daterange'"
              v-model="searchForm[field.key]"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              clearable
            />
          </div>

          <!-- 如果该行包含按钮，显示操作按钮 -->
          <div
            v-if="rowGroup.fields.includes('actions')"
            class="search-actions"
          >
            <ElButton type="primary" @click="handleSearch">
              <ElIcon><Search /></ElIcon>
              搜索
            </ElButton>
            <ElButton @click="handleReset">
              <ElIcon><Refresh /></ElIcon>
              重置
            </ElButton>
          </div>
        </div>
      </div>
    </template>

    <!-- 单个搜索项的一行布局 -->
    <div v-else-if="fields.length === 1" class="single-search-layout">
      <div class="single-search-left">
        <div class="search-label">{{ fields[0].label }}</div>
        <div class="search-input-wrapper">
          <!-- 输入框 -->
          <ElInput
            v-if="fields[0].type === 'input'"
            v-model="searchForm[fields[0].key]"
            :placeholder="fields[0].placeholder"
            clearable
            @keyup.enter="handleSearch"
          />

          <!-- 选择框（自研下拉选择组件） -->
          <DhSelect
            v-else-if="fields[0].type === 'select'"
            v-model="searchForm[fields[0].key]"
            :options="fields[0].options || []"
            :placeholder="fields[0].placeholder"
          />

          <!-- 日期选择器 -->
          <ElDatePicker
            v-else-if="fields[0].type === 'date'"
            v-model="searchForm[fields[0].key]"
            type="date"
            :placeholder="fields[0].placeholder"
            clearable
          />

          <!-- 日期范围选择器 -->
          <ElDatePicker
            v-else-if="fields[0].type === 'daterange'"
            v-model="searchForm[fields[0].key]"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            clearable
          />
        </div>
      </div>

      <div class="single-search-actions">
        <ElButton type="primary" @click="handleSearch">
          <ElIcon><Search /></ElIcon>
          搜索
        </ElButton>
        <ElButton @click="handleReset">
          <ElIcon><Refresh /></ElIcon>
          重置
        </ElButton>
      </div>
    </div>

    <!-- 多个搜索项的原有布局 -->
    <template v-else>
      <!-- 搜索字段区域 -->
      <div class="search-fields" :style="fieldsStyle">
        <div
          v-for="(field, index) in fields"
          :key="field.key"
          class="search-item"
          :style="itemStyle"
        >
          <!-- 字段标题 -->
          <div v-if="field.label" class="search-label">{{ field.label }}</div>

          <!-- 输入框 -->
          <ElInput
            v-if="field.type === 'input'"
            v-model="searchForm[field.key]"
            :placeholder="field.placeholder"
            clearable
            @keyup.enter="handleSearch"
          />

          <!-- 选择框（自研下拉选择组件） -->
          <DhSelect
            v-else-if="field.type === 'select'"
            v-model="searchForm[field.key]"
            :options="field.options || []"
            :placeholder="field.placeholder"
          />

          <!-- 日期选择器 -->
          <ElDatePicker
            v-else-if="field.type === 'date'"
            v-model="searchForm[field.key]"
            type="date"
            :placeholder="field.placeholder"
            clearable
          />

          <!-- 日期范围选择器 -->
          <ElDatePicker
            v-else-if="field.type === 'daterange'"
            v-model="searchForm[field.key]"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            clearable
          />
        </div>
      </div>

      <!-- 操作按钮区域 -->
      <div class="search-actions">
        <ElButton type="primary" @click="handleSearch">
          <ElIcon><Search /></ElIcon>
          搜索
        </ElButton>
        <ElButton @click="handleReset">
          <ElIcon><Refresh /></ElIcon>
          重置
        </ElButton>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * 通用搜索表单组件
 * 支持动态搜索项配置和自动宽度计算
 * @author 开发者
 * @date 2024-01-20
 */

import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { Search, Refresh } from '@element-plus/icons-vue'
import DhSelect from '@/components/DhSelect/index.vue'
import type { ISearchField, ISearchRow } from './types'

// 组件属性定义
interface Props {
  /** 搜索字段配置 */
  fields: ISearchField[]
  /** 多行布局配置（可选，不配置则使用默认单行布局） */
  rows?: ISearchRow[]
  /** 初始搜索值 */
  modelValue?: Record<string, any>
  /** 固定间距大小 */
  gap?: number
  /** 最小宽度 */
  minWidth?: number
  /** 最大宽度 */
  maxWidth?: number
}

// 组件事件定义
interface Emits {
  /** 搜索事件 */
  search: [values: Record<string, any>]
  /** 重置事件 */
  reset: []
  /** 值变化事件 */
  'update:modelValue': [values: Record<string, any>]
}

// 定义props
const props = withDefaults(defineProps<Props>(), {
  gap: 16,
  minWidth: 180,
  maxWidth: 300,
  modelValue: () => ({})
})

// 定义emits
const emit = defineEmits<Emits>()

// 搜索表单数据
const searchForm = ref<Record<string, any>>({})

// 初始化搜索表单
const initSearchForm = () => {
  const form: Record<string, any> = {}
  props.fields.forEach(field => {
    // 保持原有值，如果没有则使用默认值
    form[field.key] = props.modelValue[field.key] !== undefined ? props.modelValue[field.key] : ''
  })
  searchForm.value = form
}

// 计算搜索字段容器样式
const fieldsStyle = computed(() => ({
  gap: `${props.gap}px`
}))

// 计算搜索项样式
const itemStyle = computed(() => ({
  minWidth: `${props.minWidth}px`,
  maxWidth: `${props.maxWidth}px`
}))

// 计算是否使用多行布局
const useMultiRowLayout = computed(() => {
  return props.rows && props.rows.length > 0
})

// 计算多行布局的字段分组
const rowGroups = computed(() => {
  if (!useMultiRowLayout.value) return []

  return props.rows!.map(row => ({
    ...row,
    fieldConfigs: row.fields.map(fieldKey =>
      props.fields.find(field => field.key === fieldKey)
    ).filter(Boolean) as ISearchField[]
  }))
})

/**
 * 处理搜索操作
 */
const handleSearch = () => {
  emit('search', { ...searchForm.value })
}

/**
 * 处理重置操作
 */
const handleReset = () => {
  const form: Record<string, any> = {}
  props.fields.forEach(field => {
    form[field.key] = ''
  })
  searchForm.value = form
  emit('reset')
  emit('update:modelValue', { ...searchForm.value })
}

// 监听搜索表单变化
watch(
  searchForm,
  (newValue) => {
    emit('update:modelValue', { ...newValue })
  },
  { deep: true }
)

// 监听外部值变化
watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    // 只有当外部值真正发生变化时才重新初始化
    if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
      initSearchForm()
    }
  },
  { deep: true }
)

// 组件挂载时初始化
onMounted(() => {
  initSearchForm()
})
</script>

<style scoped>
/**
 * 通用搜索表单样式
 */
.dh-search-form {
  /* 使用 Element Plus 主题变量，适配浅/深色模式 */
  background: var(--el-bg-color);
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
}

/* 多行布局样式 */
.multi-row-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}

.search-row .search-item {
  flex: 1;
  min-width: 200px;
  max-width: 300px;
}

.search-row .search-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

/* 单个搜索项的一行布局样式 */
.single-search-layout {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
}

.single-search-left {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.single-search-left .search-label {
  font-size: 14px;
  /* 次级文本颜色使用主题变量 */
  color: var(--el-text-color-secondary);
  font-weight: 500;
  white-space: nowrap;
  line-height: 32px;
  margin-bottom: 0;
}

.search-input-wrapper {
  flex: 1;
  min-width: 200px;
  max-width: 400px;
}

.single-search-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

/* 搜索字段区域样式 - space-between左右对齐方案 */
.search-fields {
  display: flex;
  align-items: flex-end;
  margin-bottom: 16px;
  flex-wrap: nowrap;
}

.search-item {
  flex: 1; /* 动态宽度，平均分配剩余空间 */
  margin-right: 16px; /* 搜索项之间的间距 */
}

.search-item:last-child {
  margin-right: 0; /* 最后一个搜索项不需要右边距 */
}

/* 搜索字段标题样式 */
.search-label {
  font-size: 14px;
  /* 次级文本颜色使用主题变量 */
  color: var(--el-text-color-secondary);
  margin-bottom: 8px;
  font-weight: 500;
}

/* 表单控件在深色模式下的可见性增强 */
/* 统一输入、选择、日期等控件的可视风格 */
.dh-search-form :deep(.el-input__wrapper),
.dh-search-form :deep(.el-select__wrapper),
.dh-search-form :deep(.el-date-editor .el-input__wrapper) {
  background: var(--el-fill-color-blank);
  box-shadow: inset 0 0 0 1px var(--el-border-color-lighter) !important;
  color: var(--el-text-color-primary);
}
.dh-search-form :deep(.el-input__inner::placeholder) {
  color: var(--el-text-color-placeholder);
}
.dh-search-form :deep(.el-input__suffix .el-icon),
.dh-search-form :deep(.el-select .el-select__caret) {
  color: var(--el-text-color-secondary);
}
/* 焦点态主色强调 */
.dh-search-form :deep(.el-input__wrapper.is-focus),
.dh-search-form :deep(.el-select__wrapper.is-focus),
.dh-search-form :deep(.el-date-editor.is-active .el-input__wrapper) {
  box-shadow: inset 0 0 0 1px var(--el-color-primary) !important;
  background: var(--el-fill-color-blank);
}

/* 选择下拉弹层美化（通过 popper-class） */
:global(.dh-search-select-popper) .el-select-dropdown {
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  padding: 0;
}

/* 更高优先级的面板容器样式（popper根节点） */
:global(.el-popper.dh-search-select-popper) {
  background: #fff !important;
  border: 1px solid var(--el-border-color-lighter) !important;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06) !important;
  border-radius: 10px !important;
  padding: 0 !important;
}

/* 移除内部 wrap/list 默认内边距与外边距 */
:global(.el-popper.dh-search-select-popper) .el-select-dropdown__wrap {
  padding: 0 !important;
  margin: 0 !important;
}

:global(.el-popper.dh-search-select-popper) .el-select-dropdown__list {
  padding: 0 !important;
  margin: 0 !important;
}

:global(.dh-search-select-popper) .el-select-dropdown__item {
  color: var(--el-text-color-primary);
  border-radius: 8px;
  padding: 8px 10px;
}

:global(.dh-search-select-popper) .el-select-dropdown__item.hover,
:global(.dh-search-select-popper) .el-select-dropdown__item:hover {
  background: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
}

:global(.dh-search-select-popper) .el-select-dropdown__item.selected {
  background: var(--el-color-primary-light-8);
  color: var(--el-color-primary);
  font-weight: 600;
}

:global(.el-popper.dh-search-select-popper) .el-popper__arrow::before {
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  box-shadow: none;
}

/* 操作按钮区域样式 - 右对齐 */
.search-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* 响应式适配 */
@media (max-width: 768px) {
  /* 多行布局在移动端的适配 */
  .search-row {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-row .search-item {
    min-width: 100%;
    max-width: 100%;
  }

  .search-row .search-actions {
    justify-content: center;
  }

  /* 单个搜索项在移动端的适配 */
  .single-search-layout {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .single-search-left {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .single-search-left .search-label {
    line-height: normal;
  }

  .search-input-wrapper {
    min-width: 100%;
    max-width: 100%;
  }

  .single-search-actions {
    justify-content: center;
  }

  .search-fields {
    flex-direction: column;
    gap: 16px;
  }

  .search-item {
    width: 100%;
    max-width: none;
  }

  .search-actions {
    justify-content: center;
  }
}</style>
